import { VStack } from "@chakra-ui/react";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AwardSubCriterionAnswerFormat,
  LibraryElementTypeDto,
  WeightType,
  type AwardCriterionDto,
  type AwardSubCriterionDto,
  type LibraryElementContentDto,
  type LibraryElementDto,
  useCreateLibraryElementMutation,
  useDeleteLibraryElementMutation,
  useUpdateLibraryElementMutation,
} from "../../autogen/bff-api";
import { useApiError } from "../../common/errors/useApiError";
import { useProcurementTerms } from "../../common/terminology/useProcurementTerms";
import { AddRequirementPanel } from "../../common/library/AddRequirementPanel";
import type { AwardSubCriterionSubmitValues } from "../../common/library/fields/AwardSubCriterionFields";
import { useConfirmationModal } from "../../common/useConfirmationModal";
import { AwardCriterionModal, type AwardCriterionFormValues } from "./AwardCriterionModal";
import { AwardCriterionList } from "./AwardCriterionList";
import { AwardSubCriterionModal, type AwardSubCriterionFormValues } from "./AwardSubCriterionModal";
import { LibrarySourceSection } from "./LibrarySourceSection";

// t('Your award criteria') t('SourceMagnet standard')
// t('Delete criterion') t('Are you sure you want to delete this criterion?') t('Delete')
// t('Delete sub-criterion') t('Are you sure you want to delete this requirement?')
// t('Add sub-criterion to {{criterion}}') t('sub-criteria')

interface Props {
  elements: LibraryElementDto[];
  orgName: string;
  canManageOrg: boolean;
  addRefusal?: string;
  isSystemAdmin: boolean;
}

type DrawerState =
  | { kind: "add"; global: boolean }
  | {
      kind: "edit";
      element: LibraryElementDto;
      initialValues: AwardCriterionFormValues;
      // The form rebuilds the criterion from its own values, so its sub-criteria ride along untouched.
      subCriteria: AwardSubCriterionDto[];
    };

/** Which criterion a sub-criterion is being written into, and whether it replaces one already there. */
type SubDrawerState =
  | { kind: "sub-add"; row: Row }
  | { kind: "sub-library"; row: Row }
  | { kind: "sub-edit"; row: Row; subIndex: number; initialValues: AwardSubCriterionFormValues };

interface Row {
  element: LibraryElementDto;
  value: AwardCriterionDto;
}

const rowsOf = (elements: LibraryElementDto[]): Row[] =>
  elements.flatMap((element) => (element.content.award ? [{ element, value: element.content.award }] : []));

export const AwardCriterionLibraryTab = ({ elements, orgName, canManageOrg, addRefusal, isSystemAdmin }: Props) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const { trigger } = useApiError();
  const confirmationModal = useConfirmationModal();
  const [createLibraryElement, { isLoading: isCreating }] = useCreateLibraryElementMutation();
  const [updateLibraryElement, { isLoading: isUpdating }] = useUpdateLibraryElementMutation();
  const [deleteLibraryElement] = useDeleteLibraryElementMutation();
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [subDrawer, setSubDrawer] = useState<SubDrawerState | null>(null);
  const [drawerKey, setDrawerKey] = useState(0);

  /**
   * Weights typed into a row but not yet acknowledged by the server.
   *
   * Unlike the wizard, this page has no save coordinator holding an optimistic copy of what is being
   * edited — the rows are whatever the last fetch returned. Without this overlay a debounced write
   * leaves the field showing the stale number the moment focus leaves it, and the buyer watches their
   * own edit disappear. Keyed by element and position, and dropped again as soon as the fetched
   * element agrees.
   */
  const [pendingWeights, setPendingWeights] = useState<Record<string, number | undefined>>({});
  const weightKey = (elementId: string, subIndex: number) => `${elementId}#${subIndex}`;

  const award = elements.filter((e) => e.content.type === LibraryElementTypeDto.Award);
  const withPendingWeights = (rows: Row[]): Row[] =>
    rows.map((row) => {
      const subCriteria = row.value.subCriteria ?? [];
      if (!subCriteria.some((_, i) => weightKey(row.element.id, i) in pendingWeights)) return row;
      return {
        ...row,
        value: {
          ...row.value,
          subCriteria: subCriteria.map((sub, i) => {
            const key = weightKey(row.element.id, i);
            if (!(key in pendingWeights)) return sub;
            const weight = pendingWeights[key];
            return { ...sub, weight: weight == null ? undefined : { number: weight, type: WeightType.Percentage } };
          }),
        },
      };
    });
  const orgRows = withPendingWeights(rowsOf(award.filter((e) => !e.owner.global)));
  const globalRows = withPendingWeights(rowsOf(award.filter((e) => e.owner.global)));

  // The flush reads the rows as they stand when it fires rather than when it was scheduled, so a number
  // typed over three keystrokes is one write, and two criteria edited in the same breath are two.
  const latestRows = useRef<Row[]>([]);
  latestRows.current = [...orgRows, ...globalRows];
  const pendingKeys = useRef<string[]>([]);
  pendingKeys.current = Object.keys(pendingWeights);
  // Assigned once the write itself is defined, below — the flush only ever calls it later.
  const saveRef = useRef<((row: Row, subCriteria: AwardSubCriterionDto[]) => Promise<boolean>) | null>(null);

  const flushWeights = useMemo(
    () =>
      debounce(() => {
        const touched = new Set(pendingKeys.current.map((key) => key.slice(0, key.lastIndexOf("#"))));
        touched.forEach((elementId) => {
          const row = latestRows.current.find((r) => r.element.id === elementId);
          if (row) void saveRef.current?.(row, row.value.subCriteria ?? []);
        });
      }, 500),
    [],
  );
  // The tabs are lazy, so switching one unmounts this panel. Cancelling here would throw away a weight
  // typed within the debounce window, silently — flushing sends it.
  useEffect(() => () => flushWeights.flush(), [flushWeights]);

  // A pending weight stops being pending the moment the fetched element carries it. A failed write is
  // deliberately left pending: the row keeps what the buyer typed (the toast already said it did not
  // land) and the value rides along the next flush of that criterion rather than vanishing.
  useEffect(() => {
    setPendingWeights((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([key, weight]) => {
          const elementId = key.slice(0, key.lastIndexOf("#"));
          const subIndex = Number(key.slice(key.lastIndexOf("#") + 1));
          const stored = elements
            .find((e) => e.id === elementId)
            ?.content.award?.subCriteria?.[subIndex]?.weight?.number;
          return (stored ?? undefined) !== weight;
        }),
      );
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [elements]);

  const handleSubCriterionWeightChange = (row: Row, subIndex: number, weight: number | undefined) => {
    setPendingWeights((prev) => ({ ...prev, [weightKey(row.element.id, subIndex)]: weight }));
    flushWeights();
  };

  const openAdd = (global: boolean) => {
    setDrawerKey((k) => k + 1);
    setDrawer({ kind: "add", global });
  };

  const openEdit = (element: LibraryElementDto, value: AwardCriterionDto) => {
    setDrawerKey((k) => k + 1);
    setDrawer({
      kind: "edit",
      element,
      subCriteria: value.subCriteria ?? [],
      initialValues: {
        type: value.type,
        name: value.name ?? "",
        description: value.description,
        weight: value.weight?.number != null ? String(value.weight.number) : "",
      },
    });
  };

  const handleSubmit = async (values: AwardCriterionDto) => {
    try {
      if (drawer?.kind === "add") {
        await createLibraryElement({
          createLibraryElementRequest: {
            content: { type: LibraryElementTypeDto.Award, award: values },
            global: drawer.global,
          },
        }).unwrap();
      } else if (drawer?.kind === "edit") {
        await updateLibraryElement({
          updateLibraryElementRequest: {
            id: drawer.element.id,
            content: {
              type: LibraryElementTypeDto.Award,
              award: { ...values, id: drawer.element.content.award?.id ?? undefined },
            },
            tags: drawer.element.tags,
          },
        }).unwrap();
      }
      setDrawer(null);
    } catch (e) {
      trigger(e);
    }
  };

  /**
   * One write for any change to the requirements under a criterion: the whole element goes back with a
   * new sub-criteria list. Keeping the criterion's own id is what makes it an update rather than a new
   * element wearing the same name.
   */
  const saveSubCriteria = async (row: Row, subCriteria: AwardSubCriterionDto[]) => {
    try {
      await updateLibraryElement({
        updateLibraryElementRequest: {
          id: row.element.id,
          content: {
            type: LibraryElementTypeDto.Award,
            award: { ...row.value, id: row.element.content.award?.id ?? undefined, subCriteria },
          },
          tags: row.element.tags,
        },
      }).unwrap();
      return true;
    } catch (e) {
      trigger(e);
      return false;
    }
  };

  saveRef.current = saveSubCriteria;

  const writeSubCriteria = async (row: Row, subCriteria: AwardSubCriterionDto[]) => {
    if (await saveSubCriteria(row, subCriteria)) setSubDrawer(null);
  };

  const openSubDrawer = (state: SubDrawerState) => {
    setDrawerKey((k) => k + 1);
    setSubDrawer(state);
  };

  const handleSubCriterionSubmit = async (values: AwardSubCriterionSubmitValues) => {
    if (subDrawer?.kind !== "sub-add" && subDrawer?.kind !== "sub-edit") return;
    const existing = subDrawer.row.value.subCriteria ?? [];
    const written: AwardSubCriterionDto = {
      name: values.name,
      description: values.description,
      weight: values.weight,
      answerFormat: values.answerFormat,
      requiresDocumentation: values.requiresDocumentation,
    };
    await writeSubCriteria(
      subDrawer.row,
      subDrawer.kind === "sub-edit"
        ? existing.map((s, i) => (i === subDrawer.subIndex ? { ...s, ...written } : s))
        : [...existing, written],
    );
  };

  // Picked from the library, a requirement is copied in rather than linked: the criterion is a
  // definition of its own, and a later edit to the library element must not rewrite it.
  const handleSubCriteriaFromLibrary = async (contents: LibraryElementContentDto[]) => {
    if (subDrawer?.kind !== "sub-library") return;
    const picked = contents.flatMap((c) =>
      c.awardSubCriterion
        ? [
            {
              name: c.awardSubCriterion.name,
              description: c.awardSubCriterion.description,
              answerFormat: c.awardSubCriterion.answerFormat,
              requiresDocumentation: c.awardSubCriterion.requiresDocumentation,
            },
          ]
        : [],
    );
    if (picked.length === 0) return;
    await writeSubCriteria(subDrawer.row, [...(subDrawer.row.value.subCriteria ?? []), ...picked]);
  };

  const handleDeleteSubCriterion = (row: Row, subIndex: number) => {
    confirmationModal.show({
      title: t("Delete sub-criterion"),
      description: t("Are you sure you want to delete this requirement?"),
      confirmButtonText: t("Delete") ?? "",
      confirmButtonColor: "red",
      onConfirmed: () =>
        writeSubCriteria(
          row,
          (row.value.subCriteria ?? []).filter((_, i) => i !== subIndex),
        ),
    });
  };

  // Both source sections manage requirements the same way; only which rows they hold differs.
  const subCriterionHandlers = (rows: Row[]) => ({
    onAddSubCriterion: (i: number) => openSubDrawer({ kind: "sub-add", row: rows[i] }),
    onAddSubCriterionFromLibrary: (i: number) => openSubDrawer({ kind: "sub-library", row: rows[i] }),
    onEditSubCriterion: (i: number, subIndex: number) => {
      const sub = rows[i].value.subCriteria?.[subIndex];
      if (!sub) return;
      openSubDrawer({
        kind: "sub-edit",
        row: rows[i],
        subIndex,
        initialValues: {
          name: sub.name ?? "",
          weight: sub.weight?.number != null ? String(sub.weight.number) : "",
          description: sub.description,
          answerFormat: sub.answerFormat ?? AwardSubCriterionAnswerFormat.FreeText,
          requiresDocumentation: sub.requiresDocumentation ?? false,
        },
      });
    },
    onDeleteSubCriterion: (i: number, subIndex: number) => handleDeleteSubCriterion(rows[i], subIndex),
    onSubCriterionWeightChange: (i: number, subIndex: number, weight: number | undefined) =>
      handleSubCriterionWeightChange(rows[i], subIndex, weight),
  });

  const handleDelete = (element: LibraryElementDto) => {
    confirmationModal.show({
      title: terms.deleteCriterion,
      description: terms.deleteCriterionConfirm,
      confirmButtonText: t("Delete") ?? "",
      confirmButtonColor: "red",
      onConfirmed: async () => {
        try {
          await deleteLibraryElement({ id: element.id }).unwrap();
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  return (
    <VStack align="stretch" spacing="8">
      <LibrarySourceSection
        title={terms.yourAwardCriteria}
        scope="org"
        orgName={orgName}
        editable={canManageOrg}
        addRefusal={addRefusal}
        addLabel={terms.newCriterion}
        onAdd={() => openAdd(false)}
        isEmpty={orgRows.length === 0}
        emptyText={terms.noOwnAwardCriteria}
      >
        <AwardCriterionList
          items={orgRows.map((r) => r.value)}
          readOnly={!canManageOrg}
          onAdd={() => openAdd(false)}
          onEdit={(i) => openEdit(orgRows[i].element, orgRows[i].value)}
          onDelete={(i) => handleDelete(orgRows[i].element)}
          {...subCriterionHandlers(orgRows)}
        />
      </LibrarySourceSection>

      <LibrarySourceSection
        title={t("SourceMagnet standard")}
        scope="global"
        editable={isSystemAdmin}
        addRefusal={addRefusal}
        addLabel={terms.newCriterion}
        onAdd={() => openAdd(true)}
        isEmpty={globalRows.length === 0}
        emptyText={terms.noStandardAwardCriteria}
      >
        <AwardCriterionList
          items={globalRows.map((r) => r.value)}
          readOnly={!isSystemAdmin}
          onAdd={() => openAdd(true)}
          onEdit={(i) => openEdit(globalRows[i].element, globalRows[i].value)}
          onDelete={(i) => handleDelete(globalRows[i].element)}
          {...subCriterionHandlers(globalRows)}
        />
      </LibrarySourceSection>

      <AwardCriterionModal
        key={drawerKey}
        isOpen={drawer !== null}
        isSaving={isCreating || isUpdating}
        mode={drawer?.kind ?? "add"}
        defaultValues={drawer?.kind === "edit" ? drawer.initialValues : undefined}
        subCriteria={drawer?.kind === "edit" ? drawer.subCriteria : undefined}
        onSubmit={handleSubmit}
        onClose={() => setDrawer(null)}
      />

      <AwardSubCriterionModal
        key={`sub-${drawerKey}`}
        isOpen={subDrawer?.kind === "sub-add" || subDrawer?.kind === "sub-edit"}
        isSaving={isUpdating}
        mode={subDrawer?.kind === "sub-edit" ? "edit" : "add"}
        defaultValues={subDrawer?.kind === "sub-edit" ? subDrawer.initialValues : undefined}
        // The criterion it is written into answers where it belongs, so the form does not ask.
        inheritsCriterionType
        onSubmit={handleSubCriterionSubmit}
        onClose={() => setSubDrawer(null)}
      />

      {subDrawer?.kind === "sub-library" && (
        <AddRequirementPanel
          key={`sub-library-${drawerKey}`}
          isOpen
          onClose={() => setSubDrawer(null)}
          type={LibraryElementTypeDto.AwardSubCriterion}
          title={t("Add sub-criterion to {{criterion}}", {
            criterion: subDrawer.row.value.name?.trim() || t(subDrawer.row.value.type),
          })}
          typeNoun={t("sub-criteria")}
          initialTab="library"
          lockedToInitialTab
          // Only what belongs under this kind of criterion is on offer — the same rule the wizard's
          // panel follows when it is opened from inside a criterion.
          criterionType={subDrawer.row.value.type}
          isAlreadyInSection={(el) =>
            !!el.content.awardSubCriterion?.name &&
            (subDrawer.row.value.subCriteria ?? []).some(
              (sub) => sub.name === el.content.awardSubCriterion?.name,
            )
          }
          onAdd={(contents) => void handleSubCriteriaFromLibrary(contents)}
        />
      )}

      {confirmationModal.modal}
    </VStack>
  );
};
